<template>
	<div
		:class="['widget-host', { 'edit-mode': editMode, 'config-open': isConfigOpen }]"
		:dir="dir"
	>
		<div v-if="editMode" class="widget-host__overlay" aria-hidden="true" />

		<div v-if="editMode" class="widget-host__header">
			<span class="widget-host__drag widget-drag-handle" :title="$t('dashboard_drag_handle')" :aria-label="$t('dashboard_drag_handle')">
				<svg class="widget-host__icon" viewBox="0 0 24 24" aria-hidden="true">
					<path :d="icons.drag" fill="currentColor" />
				</svg>
			</span>
			<span class="widget-host__title">{{ title }}</span>
			<div class="widget-host__actions">
				<button
					v-if="hasConfigSchema"
					type="button"
					class="widget-host__action-btn widget-host__action-btn--configure"
					:class="{ 'is-active': isConfigOpen }"
					:title="$t('dashboard_configure')"
					:aria-label="$t('dashboard_configure')"
					:aria-expanded="isConfigOpen ? 'true' : 'false'"
					@mousedown.stop
					@touchstart.stop
					@click.stop="isConfigOpen = !isConfigOpen"
				>
					<svg class="widget-host__icon" viewBox="0 0 24 24" aria-hidden="true">
						<path :d="icons.configure" fill="currentColor" />
					</svg>
					<span class="widget-host__action-label">{{ $t('dashboard_configure') }}</span>
				</button>
				<button
					type="button"
					class="widget-host__action-btn widget-host__action-btn--remove"
					:title="$t('dashboard_remove_widget')"
					:aria-label="$t('dashboard_remove_widget')"
					@mousedown.stop
					@touchstart.stop
					@click.stop="$emit('remove')"
				>
					<svg class="widget-host__icon" viewBox="0 0 24 24" aria-hidden="true">
						<path :d="icons.remove" fill="currentColor" />
					</svg>
				</button>
			</div>
		</div>

		<div v-if="editMode && isConfigOpen" class="widget-host__config" @mousedown.stop @touchstart.stop>
			<div v-for="field in configSchema" :key="field.key" class="widget-host__config-field">
				<label :for="`${item.i}-${field.key}`">{{ $t(field.labelKey) }}</label>
				<select
					:id="`${item.i}-${field.key}`"
					:value="item.config[field.key]"
					@change="onConfigFieldChange(field.key, $event.target.value)"
				>
					<option v-for="option in field.options" :key="option.value" :value="option.value">
						{{ $t(option.labelKey) }}
					</option>
				</select>
			</div>
		</div>

		<div class="widget-host__body widget-body">
			<component :is="widgetComponent" :config="item.config" :widgetData="widgetData" />
		</div>
	</div>
</template>

<script>
import { mdiClose, mdiDragVertical, mdiPencilOutline } from '@mdi/js'
import { getWidgetDefinition, getFilteredConfigSchema, buildWidgetSiteContext } from './widgetRegistry'
import { mapGetters } from 'vuex'

export default {
	name: 'WidgetHost',
	props: {
		/** Layout item: { i, x, y, w, h, widgetType, config } */
		item: { type: Object, required: true },
		editMode: { type: Boolean, default: false },
		widgetData: { type: Object, required: false, default: () => ({}) },
		dir: { type: String, default: 'ltr' }
	},
	data() {
		return {
			isConfigOpen: false,
			icons: {
				drag: mdiDragVertical,
				configure: mdiPencilOutline,
				remove: mdiClose
			}
		}
	},
	computed: {
		...mapGetters(['currentFilteringSite', 'currentSiteInfo']),
		definition() {
			return getWidgetDefinition(this.item.widgetType)
		},
		widgetComponent() {
			return this.definition && this.definition.component
		},
		title() {
			return this.definition ? this.$t(this.definition.titleKey) : this.item.widgetType
		},
		configSchema() {
			if (!this.definition) return []
			return getFilteredConfigSchema(
				this.definition,
				buildWidgetSiteContext({
					currentFilteringSite: this.currentFilteringSite,
					currentSiteInfo: this.currentSiteInfo
				})
			)
		},
		hasConfigSchema() {
			return this.configSchema.length > 0
		}
	},
	methods: {
		onConfigFieldChange(key, value) {
			this.$emit('config-change', { ...this.item.config, [key]: value })
		}
	},
	watch: {
		editMode(value) {
			if (!value) this.isConfigOpen = false
		}
	}
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.widget-host {
	position: relative;
	height: 100%;
	width: 100%;
	min-height: 0;
	display: flex;
	flex-direction: column;

	&.edit-mode {
		&::after {
			content: '';
			position: absolute;
			inset: 0;
			border: 1px dashed #d6dee8;
			border-radius: 10px;
			pointer-events: none;
			transition: border-color 0.15s ease;
			z-index: 2;
		}

		&:hover::after,
		&.config-open::after {
			border-color: #b9c4d4;
		}
	}

	&__overlay {
		position: absolute;
		inset: 0;
		border-radius: 10px;
		background: rgba(15, 23, 42, 0.04);
		opacity: 0;
		pointer-events: none;
		transition: opacity 0.15s ease;
		z-index: 1;
	}

	&.edit-mode:hover &__overlay,
	&.edit-mode.config-open &__overlay {
		opacity: 1;
	}

	&__header {
		position: absolute;
		top: 0;
		inset-inline: 0;
		z-index: 25;
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 4px 8px;
		background: rgba(255, 255, 255, 0.94);
		border: 1px solid #e2e8f0;
		border-radius: 10px 10px 0 0;
		box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
		user-select: none;
		opacity: 0;
		transform: translateY(-4px);
		pointer-events: none;
		transition: opacity 0.15s ease, transform 0.15s ease;
	}

	&.edit-mode:hover &__header,
	&.edit-mode.config-open &__header {
		opacity: 1;
		transform: translateY(0);
	}

	&.edit-mode.config-open &__header,
	&.edit-mode:hover &__header {
		pointer-events: auto;
	}

	&__drag {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		color: #94a3b8;
		cursor: move;
	}

	&__title {
		flex: 1 1 auto;
		min-width: 0;
		font-size: 0.75rem;
		font-weight: 600;
		color: #4b5563;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__actions {
		display: flex;
		align-items: center;
		gap: 2px;
		flex-shrink: 0;
	}

	&__icon {
		width: 16px;
		height: 16px;
		display: block;
	}

	&__action-btn {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		border: none;
		background: transparent;
		color: #5f6368;
		font-size: 0.7rem;
		font-weight: 600;
		line-height: 1;
		padding: 4px 6px;
		border-radius: 6px;
		cursor: pointer;
		transition: background 0.15s ease, color 0.15s ease;

		&:hover {
			background: rgba(0, 0, 0, 0.06);
		}

		&--configure {
			color: #389ceb;

			&:hover,
			&.is-active {
				background: rgba(56, 156, 235, 0.12);
				color: #2d8cd9;
			}
		}

		&--remove {
			padding-inline: 5px;

			&:hover {
				background: rgba(194, 48, 70, 0.1);
				color: #c23046;
			}
		}
	}

	&__action-label {
		max-width: 0;
		overflow: hidden;
		white-space: nowrap;
		transition: max-width 0.15s ease;
	}

	&__action-btn--configure:hover &__action-label,
	&__action-btn--configure.is-active &__action-label {
		max-width: 6rem;
	}

	&__config {
		position: absolute;
		top: 34px;
		inset-inline-end: 6px;
		z-index: 30;
		background: white;
		border: 1px solid #d6dae1;
		border-radius: 8px;
		box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
		padding: 10px 12px;
		min-width: 180px;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	&__config-field {
		display: flex;
		flex-direction: column;
		gap: 3px;

		label {
			font-size: 0.7rem;
			font-weight: 600;
			color: #6b7280;
		}

		select {
			font-size: 0.8rem;
			padding: 4px 6px;
			border: 1px solid #d6dae1;
			border-radius: 6px;
			background: white;
			color: #374151;
		}
	}

	&__body {
		flex: 1 1 0;
		min-height: 0;
		display: flex;
		flex-direction: column;

		> * {
			flex: 1;
			min-height: 0;
		}
	}
}
</style>
